import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SessionStorageKeys } from 'chaika-enums';

import { store } from '@store';
import { authUserQueryOptions } from '@user/queries/authUserQueries';

export interface BrowserAutofillValues {
    email?: string;
    password?: string;
}

interface UseAuthPageBootstrapParams {
    isBrowserAutofillCheckNeeded: boolean;
    onBrowserAutofillDetected(values: BrowserAutofillValues): void;
}

export function useAuthPageBootstrap({
    isBrowserAutofillCheckNeeded,
    onBrowserAutofillDetected,
}: UseAuthPageBootstrapParams): void {
    const queryClient = useQueryClient();

    const [wasAuthorizedAtMount] = useState(
        () => !!queryClient.getQueryData(authUserQueryOptions.current().queryKey)?.id,
    );
    const { isSuccess, isError } = useQuery({ ...authUserQueryOptions.current(), retry: false });
    const isSettled = isSuccess || isError;

    useEffect(() => {
        if (wasAuthorizedAtMount) {
            window.location.href = '/';
            return;
        }

        if (!isSettled) {
            return;
        }

        sessionStorage.setItem(SessionStorageKeys.FromAuthPage, 'true');

        if (isBrowserAutofillCheckNeeded) {
            setTimeout(() => {
                const emailInput = document.querySelector<HTMLInputElement>('input[name="email"]');
                const passwordInput = document.querySelector<HTMLInputElement>('input[name="password"]');

                onBrowserAutofillDetected({
                    email: emailInput?.value,
                    password: passwordInput?.value,
                });
            }, 500);
        }

        store.app.withoutHeader = true;
        store.app.withoutLeftMenu = true;

        return () => {
            store.app.withoutHeader = false;
            store.app.withoutLeftMenu = false;
        };
    }, [wasAuthorizedAtMount, isSettled, isBrowserAutofillCheckNeeded, onBrowserAutofillDetected]);
}
